/**
 * SetupAccountPage — Custom invite-token flow (Feb 19, 2026).
 *
 * Journey:
 *   1) Admin sends a direct/waitlist invite. Backend mints a one-time
 *      sha256-hashed token and emails a Resend-branded link of the form
 *      https://bayenah.com/setup-account?token=<raw_token>
 *   2) User clicks the link. This page reads ?token=, calls
 *      GET /api/auth/validate-invite-token to render the email + name.
 *   3) User picks a password and submits to POST /api/auth/complete-setup.
 *      Backend sets the password via Supabase Admin, signs the user in,
 *      and returns {access_token, refresh_token}.
 *   4) We hand those to supabase.auth.setSession(), drop the local
 *      bayenah_password_setup_complete flag, and navigate to /chat.
 *
 * No Supabase magic-link is involved anywhere. We control the entire
 * redirect path, so the Supabase Redirect URLs allowlist is irrelevant.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { safeFetch } from '@/lib/safeFetch';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const BACKEND_URL = getBackendBaseUrl();

export default function SetupAccountPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Validate on load. The endpoint is read-only — token isn't consumed.
  useEffect(() => {
    if (!token) {
      setTokenError('No invite token found. Please use the link from your invitation email.');
      setValidating(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Use safeFetch — bypasses the Emergent monitoring fetch-patch
        // that drains response bodies on non-2xx, which would otherwise
        // trigger "body stream already read" when this token is
        // invalid/used/expired (i.e. exactly the cases this UI handles).
        const res = await safeFetch(
          `${BACKEND_URL}/api/auth/validate-invite-token?token=${encodeURIComponent(token)}`
        );
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setTokenError(data?.detail || 'Invalid invite link.');
        } else {
          setEmail(data.email || '');
          const raw = (data.full_name || '').trim().split(/\s+/)[0] || '';
          if (raw) {
            setFirstName(raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase());
          }
        }
      } catch (_e) {
        if (!cancelled) setTokenError('Could not validate invite link. Please try again.');
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async () => {
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      // Use safeFetch — see note in the validate effect above. Without
      // it, the platform monitoring drains the body on any 4xx/5xx
      // (expired token, password too weak, Supabase error) and our
      // `await res.json()` throws "body stream already read" before
      // we can show the user a useful error message.
      const res = await safeFetch(`${BACKEND_URL}/api/auth/complete-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Setup failed. Please try again.');

      if (!data.access_token || !data.refresh_token) {
        throw new Error('Setup succeeded but session was not returned. Please sign in manually.');
      }

      // Hydrate Supabase client-side session — onAuthStateChange will
      // emit SIGNED_IN, AuthContext picks up the user, and downstream
      // app routes see a logged-in user.
      await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });

      // Persist the local flag too — ProtectedRoute and the next
      // SIGNED_IN handler use it as a secondary "setup-done" signal.
      try { localStorage.setItem('bayenah_password_setup_complete', 'true'); } catch (_e) { /* ignore */ }
      navigate('/chat', { replace: true });
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Loading ───────────────────────────────────────────────
  if (validating) {
    return (
      <div
        data-testid="setup-account-loading"
        style={{
          minHeight: '100vh',
          background: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255,255,255,0.3)',
          fontSize: 13,
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
        }}
      >
        Validating your invite...
      </div>
    );
  }

  // ─── Token error ───────────────────────────────────────────
  if (tokenError) {
    return (
      <div
        data-testid="setup-account-token-error"
        style={{
          minHeight: '100vh',
          background: '#000',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
        }}
      >
        <img src="/brand/mark-white.png" alt="Bayenah" style={{ height: 20, opacity: 0.5 }} />
        <div
          style={{
            background: '#0D0D0D',
            border: '1px solid #1A1A1A',
            borderRadius: 4,
            padding: 32,
            width: '100%',
            maxWidth: 380,
            boxSizing: 'border-box',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 15, color: '#fff', marginBottom: 10, fontWeight: 600 }}>
            Invite link issue
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.45)',
              lineHeight: 1.7,
              marginBottom: 20,
            }}
            data-testid="setup-account-token-error-message"
          >
            {tokenError}
          </div>
          <a
            href="mailto:hello@bayenah.com"
            style={{
              display: 'block',
              background: '#fff',
              color: '#000',
              padding: 10,
              borderRadius: 3,
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
              textAlign: 'center',
            }}
            data-testid="setup-account-contact-link"
          >
            Contact hello@bayenah.com
          </a>
        </div>
      </div>
    );
  }

  // ─── Setup form ────────────────────────────────────────────
  return (
    <div
      data-testid="setup-account-page"
      style={{
        minHeight: '100vh',
        background: '#000',
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
      }}
    >
      {/* Logo lockup */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
        <img
          src="/brand/mark-white.png"
          alt="Bayenah"
          style={{ height: 20, opacity: 0.7 }}
          draggable={false}
        />
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.6)',
            letterSpacing: '0.04em',
          }}
        >
          Bayenah
        </span>
      </div>

      {/* Card */}
      <div
        style={{
          background: '#0D0D0D',
          border: '1px solid #1A1A1A',
          borderRadius: 4,
          padding: 36,
          width: '100%',
          maxWidth: 380,
          boxSizing: 'border-box',
        }}
      >
        <div
          data-testid="setup-account-greeting"
          style={{
            fontFamily: 'Georgia, serif',
            fontSize: 22,
            fontWeight: 700,
            color: '#fff',
            marginBottom: 8,
          }}
        >
          {firstName ? `Welcome, ${firstName}.` : 'Welcome to Bayenah.'}
        </div>

        <div
          style={{
            fontSize: 13,
            color: 'rgba(255,255,255,0.35)',
            lineHeight: 1.7,
            marginBottom: 24,
          }}
        >
          Set a password for your account. You will use it to sign in from any device.
        </div>

        {/* Email — read-only echo so users know which account they're activating */}
        <div
          data-testid="setup-account-email-display"
          style={{
            fontSize: 13,
            color: 'rgba(255,255,255,0.55)',
            marginBottom: 20,
            padding: '10px 14px',
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 3,
            border: '1px solid #1A1A1A',
            wordBreak: 'break-all',
          }}
        >
          {email}
        </div>

        {/* Password */}
        <div style={{ marginBottom: 14 }}>
          <label
            htmlFor="setup-password"
            style={{
              fontSize: 11,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.3)',
              display: 'block',
              marginBottom: 6,
            }}
          >
            Password
          </label>
          <input
            id="setup-password"
            data-testid="setup-account-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            style={{
              width: '100%',
              background: '#000',
              border: '1px solid #222',
              borderRadius: 3,
              padding: '10px 14px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Confirm */}
        <div style={{ marginBottom: 24 }}>
          <label
            htmlFor="setup-confirm"
            style={{
              fontSize: 11,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.3)',
              display: 'block',
              marginBottom: 6,
            }}
          >
            Confirm password
          </label>
          <input
            id="setup-confirm"
            data-testid="setup-account-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder="Same password again"
            autoComplete="new-password"
            style={{
              width: '100%',
              background: '#000',
              border: '1px solid #222',
              borderRadius: 3,
              padding: '10px 14px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {error && (
          <div
            data-testid="setup-account-error"
            style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.55)',
              marginBottom: 16,
              lineHeight: 1.5,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving || !password || !confirm}
          data-testid="setup-account-submit"
          style={{
            width: '100%',
            background: '#fff',
            color: '#000',
            border: 'none',
            borderRadius: 3,
            padding: 12,
            fontSize: 14,
            fontWeight: 600,
            cursor: saving || !password || !confirm ? 'not-allowed' : 'pointer',
            opacity: saving || !password || !confirm ? 0.5 : 1,
            letterSpacing: '0.02em',
          }}
        >
          {saving ? 'Creating account...' : 'Create account'}
        </button>
      </div>

      <div
        style={{
          marginTop: 20,
          fontSize: 11,
          color: 'rgba(255,255,255,0.15)',
          textAlign: 'center',
        }}
      >
        bayenah.com &middot; hello@bayenah.com
      </div>
    </div>
  );
}
